/* global Word */

import { useState } from "react";
import Logger from "@/utility/logger";
import { formatSection as rawFormatDocument } from "./useWordDocumentUtils/wordFormatting";
import {
  getAllBookmarks as rawGetAllBookmarks,
  hasSectionBookmarks as rawHasSectionBookmarks,
  hasSection as rawHasSection,
  scrollToSection as rawScrollToSection,
  scrollToGroundingReference as rawScrollToGroundingReference,
} from "./useWordDocumentUtils/wordBookmarks";
import {
  insertAtSection as rawInsertAtSection,
  insertTemplateDocument as rawInsertTemplateDocument,
} from "./useWordDocumentUtils/wordInsertion";
import { createTable as rawCreateTable } from "./useWordDocumentUtils/wordTable";
import {
  createCaption as rawCreateCaption,
  createCaptionsForSection as rawCreateCaptionsForSection,
} from "./useWordDocumentUtils/wordCaptions";
import {
  getSelectedText as rawGetSelectedText,
  isSelectedText as rawIsSelectedText,
  insertTextAtSelection as rawInsertTextAtSelection,
  insertTextAtKey as rawInsertTextAtKey,
  insertTextAtSection as rawInsertTextAtSection,
  insertTextIncrementally as rawInsertTextIncrementally,
} from "./useWordDocumentUtils/wordText";
import { insertImage as rawInsertImage } from "./useWordDocumentUtils/wordImages";

import { applyConfirmedStyle } from "@/lib/styleService";
import { JENLY_REFERENCE_TAG_PREFIX } from "@/utility/groundingMarkers";
import { createMissingSectionBookmarkError, isMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";

function withErrorHandling(fn, setShowError) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (isMissingSectionBookmarkError(error)) {
        throw error;
      }

      Logger.error("useWordDocument.ts: Error in Word document operation:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: error.name,
        message: error.message,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context:
            error.name === "RichApi.Error"
              ? {
                  errorLocation: error.debugInfo?.errorLocation,
                  statement: error.debugInfo?.statement,
                  surroundingStatements: error.debugInfo?.surroundingStatements,
                }
              : undefined,
        },
      });
    }
  };
}

const removeGroundingContentControlsInRange = async (
  context: Word.RequestContext,
  range: Word.Range,
  keepContent = true
) => {
  const contentControls = range.contentControls;
  if (!contentControls) return;

  contentControls.load("items/tag");
  await context.sync();

  for (const control of contentControls.items) {
    if (!String(control.tag || "").startsWith(`${JENLY_REFERENCE_TAG_PREFIX}:`)) {
      continue;
    }

    try {
      control.delete(keepContent);
    } catch (error) {
      Logger.warn("useWordDocument.ts: Failed to remove grounding content control:", error);
    }
  }

  await context.sync();
};

const hideGroundingMarkersInRange = async (context: Word.RequestContext, range: Word.Range) => {
  const contentControls = range.contentControls;
  if (!contentControls) return;

  contentControls.load("items/tag");
  await context.sync();

  for (const control of contentControls.items) {
    if (!String(control.tag || "").startsWith(`${JENLY_REFERENCE_TAG_PREFIX}:`)) {
      continue;
    }

    try {
      control.insertText("\u200b", Word.InsertLocation.replace);
      control.appearance = Word.ContentControlAppearance.hidden;
    } catch (error) {
      Logger.warn("useWordDocument.ts: Failed to hide grounding marker:", error);
    }
  }

  await context.sync();
};

const useWordDocument = (setShowError) => {
  const [trackChanges, setTrackChanges] = useState(false);

  const formatSection = withErrorHandling(rawFormatDocument, setShowError);

  const getAllBookmarks = withErrorHandling(rawGetAllBookmarks, setShowError);
  const hasSection = withErrorHandling(rawHasSection, setShowError);
  const hasSectionBookmarks = rawHasSectionBookmarks;

  const scrollToSection = withErrorHandling(rawScrollToSection, setShowError);
  const scrollToGroundingReference = withErrorHandling(rawScrollToGroundingReference, setShowError);

  const insertTextAtKey = withErrorHandling(rawInsertTextAtKey, setShowError);

  const insertTextAtSection = withErrorHandling(rawInsertTextAtSection, setShowError);

  const insertAtSection = withErrorHandling(rawInsertAtSection, setShowError);

  const insertTemplateDocument = withErrorHandling(rawInsertTemplateDocument, setShowError);

  const insertTextIncrementally = withErrorHandling(rawInsertTextIncrementally, setShowError);

  const insertTextAtSelection = withErrorHandling(rawInsertTextAtSelection, setShowError);

  const createTable = withErrorHandling(rawCreateTable, setShowError);

  const createCaption = withErrorHandling(rawCreateCaption, setShowError);
  const createCaptionsForSection = withErrorHandling(rawCreateCaptionsForSection, setShowError);

  const getSelectedText = withErrorHandling(rawGetSelectedText, setShowError);

  const isSelectedText = withErrorHandling(rawIsSelectedText, setShowError);

  const insertImage = withErrorHandling(rawInsertImage, setShowError);

  // Toggles the change tracking mode in the Word document
  const handleTrackChangesToggle = async () => {
    await Word.run(async (context) => {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();

      if (trackChanges === false) {
        // when track changes is turned on, word starts to visually record every single edit made to the document
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      } else {
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
      }

      await context.sync();
    });
    if (trackChanges) {
      setTrackChanges(false);
    } else {
      setTrackChanges(true);
    }
  };

  // Declines changes made between bookmarks containing a target word in the Word document
  const declineChanges = async (targetWord: string) => {
    await Word.run(async (context) => {
      const startBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_END");

      startBookmark.load("isNullObject");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        if (startBookmark.isNullObject) {
          Logger.warn("useWordDocument.ts: Start bookmark was not found.", targetWord);
        }
        if (endBookmark.isNullObject) {
          Logger.warn("useWordDocument.ts: End bookmark was not found.", targetWord);
        }
        Logger.warn("useWordDocument.ts: One or both bookmarks were not found.", targetWord);
        throw createMissingSectionBookmarkError();
      }

      const afterStart = startBookmark.getRange(Word.RangeLocation.after);
      const para = afterStart.insertParagraph(" ", Word.InsertLocation.after);
      const afterPara = para.getRange(Word.RangeLocation.after);
      const combinedRange = afterPara.expandTo(endBookmark);

      await removeGroundingContentControlsInRange(context, combinedRange);

      const tablesInRange = combinedRange.tables;
      tablesInRange.load("items");
      await context.sync();

      // Delete tables with error handling
      for (const table of tablesInRange.items) {
        try {
          table.delete();
        } catch (error) {
          Logger.warn("useWordDocument.ts: Failed to delete table, it may have already been removed:", error);
        }
      }

      // Delete the combined range with error handling
      try {
        combinedRange.delete();
      } catch (error) {
        Logger.warn("useWordDocument.ts: Failed to delete range, it may have already been removed:", error);
      }

      await context.sync();

      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");

      bookmarkRange.load("isNullObject");
      await context.sync();

      if (bookmarkRange.isNullObject) {
        Logger.error("useWordDocument.ts: Bookmark not found!", targetWord);
        throw createMissingSectionBookmarkError();
      }

      const paragraph = bookmarkRange.insertParagraph("", Word.InsertLocation.after);
      await context.sync();

      if (!startBookmark.isNullObject) {
        let insertionPoint: Word.Range = paragraph.getRange(Word.RangeLocation.end);

        const chunkRange = insertionPoint.insertText(
          "Jenly.AI generated content will be inserted here.",
          Word.InsertLocation.end
        );
        await context.sync();
        chunkRange.styleBuiltIn = Word.BuiltInStyleName.normal;
        chunkRange.font.color = "#e7e6e6";
        // combinedRange.font.hidden = false; // Produces error in Word Online
        chunkRange.font.name = "Arial";
        chunkRange.font.size = 10;
        chunkRange.font.bold = false;
        chunkRange.font.italic = false;
        chunkRange.font.highlightColor = null;
      }

      combinedRange.load("text, paragraphs");
      await context.sync();
    });
  };

  // Accepts changes made between bookmarks containing a target word in the Word document
  const acceptChanges = async (targetWord: string) => {
    await Word.run(async (context) => {
      const startBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_END");

      startBookmark.load("isNullObject, end");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        if (startBookmark.isNullObject) {
          Logger.warn("useWordDocument.ts: Start bookmark was not found.", targetWord);
        }
        if (endBookmark.isNullObject) {
          Logger.warn("useWordDocument.ts: End bookmark was not found.", targetWord);
        }
        Logger.warn("useWordDocument.ts: One or both bookmarks were not found.", targetWord);
        throw createMissingSectionBookmarkError();
      }

      // const paragraph = startBookmark.insertParagraph("", Word.InsertLocation.after);
      await context.sync();

      const beforeEnd = endBookmark.getRange(Word.RangeLocation.start);
      const combinedRange = startBookmark.expandTo(beforeEnd);

      await hideGroundingMarkersInRange(context, combinedRange);
      await applyConfirmedStyle(combinedRange);
    });
  };

  // Updates the first Table of Contents (TOC) found in the Word document
  // const updateTOC = async () => {
  //   await Word.run(async (context) => {
  //     const tocs = context.document.body.tablesOfContents;
  //     tocs.load("items");
  //     await context.sync();

  //     console.log("Found", tocs.items.length, "TOC(s)");

  //     if (tocs.items.length > 0) {
  //       // update the first TOC found
  //       tocs.items[0].update();
  //       await context.sync();
  //       console.log("✅ TOC updated!");
  //     } else {
  //       console.warn("⚠️ No TOC found in the document.");
  //     }
  //   });
  // };

  return {
    trackChanges,
    handleTrackChangesToggle,
    insertTemplateDocument,
    formatSection,
    getAllBookmarks,
    insertTextAtSection,
    insertAtSection,
    hasSectionBookmarks,
    hasSection,
    scrollToSection,
    scrollToGroundingReference,
    insertTextIncrementally,
    createTable,
    createCaption,
    createCaptionsForSection,
    acceptChanges,
    declineChanges,
    insertTextAtKey,
    getSelectedText,
    isSelectedText,
    insertTextAtSelection,
    insertImage,
  };
};

export default useWordDocument;
